import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UserPlus,
  Pencil,
  Power,
  Trash2,
  KeyRound,
  Loader2,
  Mail,
  Phone as PhoneIcon,
  ShieldCheck,
  Users as UsersIcon,
  AlertCircle,
  AlertTriangle,
} from "lucide-react";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import UserFormDialog from "@/components/UserFormDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import HardDeleteDialog from "@/components/HardDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  resetPassword,
  adminHardDeleteUser,
} from "@/lib/admin";
import { formatApiError } from "@/lib/api";

const ROLE_LABEL = { admin: "Administrateur", agent: "Agent" };

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, target, ...}
  const [hardDelUser, setHardDelUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const [resetResult, setResetResult] = useState(null); // {password, email}

  // Guard
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Accès réservé aux administrateurs.");
      navigate("/home", { replace: true });
    }
  }, [user, navigate]);

  const reload = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await listUsers({ includeInactive });
      setUsers(r.items);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive, user]);

  const handleCreate = async (payload) => {
    setBusy(true);
    try {
      await createUser(payload);
      toast.success("Utilisateur créé.");
      setCreateOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async (payload) => {
    setBusy(true);
    try {
      await updateUser(editing.id, payload);
      toast.success("Utilisateur mis à jour.");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (target) => {
    setBusy(true);
    try {
      await updateUser(target.id, { is_active: !target.is_active });
      toast.success(target.is_active ? "Compte désactivé." : "Compte réactivé.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleDelete = async (target) => {
    setBusy(true);
    try {
      await deleteUser(target.id);
      toast.success("Compte supprimé (désactivé).");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleResetPassword = async (target) => {
    setBusy(true);
    try {
      const r = await resetPassword(target.id);
      setResetResult({ email: target.email, password: r.password });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleHardDeleteUser = async () => {
    if (!hardDelUser) return;
    setBusy(true);
    try {
      await adminHardDeleteUser(hardDelUser.id);
      toast.success(
        `${hardDelUser.full_name} supprimé${
          hardDelUser.full_name?.includes(" ") ? "" : "(e)"
        } définitivement.`
      );
      setHardDelUser(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[var(--sa-subtle)] pb-24" data-testid="admin-screen">
      <AppTopbar subtitle="Administration" />
      <main className="mx-auto max-w-3xl px-4 pt-5">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="flex items-center gap-2 text-[22px] font-bold tracking-tight text-slate-900">
              <UsersIcon size={20} className="text-[var(--sa-blue)]" />
              Utilisateurs
            </h1>
            <p className="mt-0.5 text-[13px] text-slate-500">
              {users.length} compte{users.length > 1 ? "s" : ""}
              {includeInactive ? " (incluant inactifs)" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIncludeInactive((v) => !v)}
              data-testid="toggle-include-inactive"
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              {includeInactive ? "Masquer les inactifs" : "Voir les inactifs"}
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              data-testid="admin-new-user"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--sa-blue)] px-4 py-1.5 text-[13px] font-semibold text-white shadow-sm hover:bg-[var(--sa-blue-dark)]"
            >
              <UserPlus size={14} /> Nouvel utilisateur
            </button>
          </div>
        </motion.div>

        {error ? (
          <div className="mt-5 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-12 flex items-center justify-center text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-2.5" data-testid="admin-users-list">
            {users.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                isSelf={u.id === user.id}
                onEdit={() => setEditing(u)}
                onToggleActive={() => setConfirm({ type: "toggle", target: u })}
                onDelete={() => setConfirm({ type: "delete", target: u })}
                onResetPassword={() => setConfirm({ type: "reset", target: u })}
                onHardDelete={() => setHardDelUser(u)}
              />
            ))}
            {users.length === 0 ? (
              <div className="rounded-[16px] border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
                <p className="text-[14px] font-semibold text-slate-700">
                  Aucun utilisateur à afficher.
                </p>
                <p className="mt-1 text-[12.5px] text-slate-500">
                  Cliquez sur « Nouvel utilisateur » pour démarrer.
                </p>
              </div>
            ) : null}
          </div>
        )}

        <p className="mt-10 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>
      <BottomNav />

      {/* Create dialog */}
      <UserFormDialog
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        busy={busy}
      />

      {/* Edit dialog */}
      <UserFormDialog
        mode="edit"
        open={Boolean(editing)}
        onOpenChange={(o) => !o && setEditing(null)}
        initial={editing}
        isSelf={editing && editing.id === user.id}
        onSubmit={handleEdit}
        busy={busy}
      />

      {/* Toggle active confirm */}
      <ConfirmDialog
        open={confirm?.type === "toggle"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={
          confirm?.target?.is_active
            ? `Désactiver ${confirm?.target?.full_name} ?`
            : `Réactiver ${confirm?.target?.full_name} ?`
        }
        description={
          confirm?.target?.is_active
            ? "Ce compte ne pourra plus se connecter ni recevoir de notifications jusqu'à réactivation."
            : "Ce compte pourra à nouveau se connecter."
        }
        confirmLabel={confirm?.target?.is_active ? "Désactiver" : "Réactiver"}
        tone={confirm?.target?.is_active ? "danger" : "primary"}
        loading={busy}
        onConfirm={() => handleToggleActive(confirm.target)}
        testId="confirm-toggle-active"
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={confirm?.type === "delete"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Supprimer ${confirm?.target?.full_name} ?`}
        description={
          "Le compte sera désactivé (suppression douce). Les alertes liées sont préservées dans l'historique."
        }
        confirmLabel="Supprimer"
        tone="danger"
        loading={busy}
        onConfirm={() => handleDelete(confirm.target)}
        testId="confirm-delete-user"
      />

      {/* Reset password confirm */}
      <ConfirmDialog
        open={confirm?.type === "reset"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Réinitialiser le mot de passe de ${confirm?.target?.full_name} ?`}
        description={
          "Un nouveau mot de passe aléatoire sera généré et affiché une seule fois. Notez-le et transmettez-le à l'utilisateur de manière sûre."
        }
        confirmLabel="Réinitialiser"
        tone="primary"
        loading={busy}
        onConfirm={() => handleResetPassword(confirm.target)}
        testId="confirm-reset-password"
      />

      {/* Reset result modal */}
      <ConfirmDialog
        open={Boolean(resetResult)}
        onOpenChange={(o) => !o && setResetResult(null)}
        title="Mot de passe réinitialisé"
        description={
          resetResult
            ? `Email : ${resetResult.email}\nNouveau mot de passe : ${resetResult.password}\n\nNotez-le maintenant — il ne sera plus affiché.`
            : ""
        }
        confirmLabel="J'ai noté le mot de passe"
        cancelLabel="Fermer"
        tone="primary"
        onConfirm={() => setResetResult(null)}
        testId="reset-result-dialog"
      />

      {/* HARD delete user — typed-confirmation */}
      <HardDeleteDialog
        open={Boolean(hardDelUser)}
        onOpenChange={(o) => !o && setHardDelUser(null)}
        title="Suppression définitive"
        description={
          hardDelUser
            ? `Le compte de ${hardDelUser.full_name} (${hardDelUser.email}) sera supprimé de la base de données.\n\nL'historique des alertes liées sera conservé grâce à l'instantané (snapshot) figé au moment de leur création.`
            : ""
        }
        loading={busy}
        onConfirm={handleHardDeleteUser}
        testId="hard-delete-user"
      />
    </div>
  );
}

function UserCard({
  user,
  isSelf,
  onEdit,
  onToggleActive,
  onDelete,
  onResetPassword,
  onHardDelete,
}) {
  const initials = (user.full_name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  const isAdmin = user.role === "admin";
  return (
    <div
      data-testid={`user-card-${user.email}`}
      className={`sa-card flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 ${
        user.is_active ? "" : "opacity-70"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[14px] font-bold text-white"
          style={{ background: isAdmin ? "#0F172A" : "#2563EB" }}
        >
          {initials || "?"}
        </div>
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <p className="truncate text-[14px] font-semibold text-slate-900">
              {user.full_name}
              {isSelf ? <span className="ml-1 text-[11px] font-medium text-slate-400">(vous)</span> : null}
            </p>
            <span
              className="inline-flex h-4 items-center rounded-full px-1.5 text-[9.5px] font-bold uppercase tracking-wider"
              style={{
                background: isAdmin ? "#0F172A" : "#DBEAFE",
                color: isAdmin ? "white" : "#1E40AF",
              }}
            >
              {ROLE_LABEL[user.role] || user.role}
            </span>
            {!user.is_active ? (
              <span className="inline-flex h-4 items-center rounded-full bg-slate-100 px-1.5 text-[9.5px] font-bold uppercase tracking-wider text-slate-500">
                Inactif
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Mail size={11} /> {user.email}
            </span>
            {user.phone ? (
              <span className="inline-flex items-center gap-1">
                <PhoneIcon size={11} /> {user.phone}
              </span>
            ) : null}
            {user.notifications_enabled ? null : (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <AlertCircle size={11} /> notifications coupées
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 sm:shrink-0">
        <IconButton
          icon={Pencil}
          label="Modifier"
          onClick={onEdit}
          testId={`user-edit-${user.email}`}
        />
        <IconButton
          icon={KeyRound}
          label="Réinitialiser mot de passe"
          onClick={onResetPassword}
          testId={`user-reset-${user.email}`}
        />
        <IconButton
          icon={Power}
          label={user.is_active ? "Désactiver" : "Réactiver"}
          onClick={onToggleActive}
          disabled={isSelf}
          tone={user.is_active ? "warning" : "success"}
          testId={`user-toggle-${user.email}`}
        />
        <IconButton
          icon={Trash2}
          label="Supprimer"
          onClick={onDelete}
          disabled={isSelf}
          tone="danger"
          testId={`user-delete-${user.email}`}
        />
        {!user.is_active && !isSelf ? (
          <IconButton
            icon={AlertTriangle}
            label="Supprimer définitivement"
            onClick={onHardDelete}
            tone="danger-strong"
            testId={`user-hard-delete-${user.email}`}
          />
        ) : null}
      </div>
    </div>
  );
}

function IconButton({ icon: Icon, label, onClick, disabled, tone = "neutral", testId }) {
  const tones = {
    neutral: "text-slate-600 hover:bg-slate-100",
    danger: "text-red-600 hover:bg-red-50",
    "danger-strong":
      "bg-red-600 text-white shadow-sm ring-1 ring-red-700/30 hover:bg-red-700",
    warning: "text-amber-600 hover:bg-amber-50",
    success: "text-emerald-600 hover:bg-emerald-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Action indisponible sur votre propre compte" : label}
      data-testid={testId}
      className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone]}`}
    >
      <Icon size={14} strokeWidth={2.2} />
    </button>
  );
}
